import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FileSignature, Download, ShieldCheck } from "lucide-react";
import LegalTabs from "@/components/LegalTabs";

const Section = ({ n, title, id, children, testId }) => (
    <section id={id} className="scroll-mt-24" data-testid={testId}>
        <h2 className="font-heading font-medium text-xl tracking-tight text-foreground mb-3">
            <span className="text-emerald-600 dark:text-emerald-400 tabular-nums mr-2">{n}.</span>{title}
        </h2>
        <div className="text-sm text-muted-foreground space-y-3 leading-relaxed">{children}</div>
    </section>
);

export default function Dpa() {
    return (
        <div className="bg-background" data-testid="dpa-page">
            <Helmet>
                <title>Data Processing Agreement · Simonara Talent Solutions</title>
                <meta name="description" content="Standard Data Processing Agreement template — the contractual terms governing how Simonara processes personal data on behalf of employer customers." />
                <link rel="canonical" href="/dpa" />
            </Helmet>

            <section className="pt-16 pb-2">
                <div className="mx-auto max-w-4xl px-6">
                    <p className="overline inline-flex items-center gap-1"><FileSignature size={11} /> Legal · Employers</p>
                    <h1 className="mt-2 font-heading font-medium tracking-tight text-4xl sm:text-5xl text-foreground">
                        Data Processing Agreement
                    </h1>
                    <p className="mt-3 text-base text-muted-foreground max-w-2xl">
                        The contractual terms governing how Simonara processes personal data on behalf of employer customers. Applicable to every paid workspace — countersigned copies available on request. Version 2026-02-15.
                    </p>
                    <a
                        href="mailto:privacy@simonaratalent.us?subject=DPA%20Countersign%20Request"
                        className="mt-5 inline-flex items-center gap-2 btn-green text-sm"
                        data-testid="dpa-request-countersign"
                    >
                        <Download size={14} /> Request a counter-signed DPA
                    </a>
                </div>
            </section>
            <LegalTabs />

            <section className="pb-20 pt-8">
                <div className="mx-auto max-w-4xl px-6 space-y-8">

                    <Section n="1" id="parties" title="Parties &amp; scope" testId="dpa-parties">
                        <p>
                            This Data Processing Agreement ("DPA") is entered into between the employer customer identified in the applicable order form ("<strong className="text-foreground">Controller</strong>") and Simonara Talent Solutions, LLC ("<strong className="text-foreground">Processor</strong>") and forms part of the underlying Simonara <Link to="/terms" className="underline text-foreground font-medium">Terms of Service</Link>.
                        </p>
                        <p>It governs Processor's processing of Personal Data on behalf of the Controller in the course of providing the Simonara Talent Solutions platform.</p>
                    </Section>

                    <Section n="2" id="purpose" title="Processing purpose" testId="dpa-purpose">
                        <p>Processor will process Personal Data solely to:</p>
                        <ul className="list-disc pl-5 space-y-1.5">
                            <li>Operate the Simonara platform for the Controller (posting jobs, receiving applications, AI matching, background verification workflows, communications).</li>
                            <li>Deliver features Controller has purchased or activated.</li>
                            <li>Provide customer support at Controller's request.</li>
                            <li>Comply with the DPA, the Terms of Service, and applicable law.</li>
                        </ul>
                        <p>No secondary use for advertising, training third-party AI, or data brokering.</p>
                    </Section>

                    <Section n="3" id="data-types" title="Categories of data &amp; data subjects" testId="dpa-data-types">
                        <p><strong className="text-foreground">Data subjects:</strong> Controller's employees + hiring team, and candidates who apply to Controller's roles.</p>
                        <p><strong className="text-foreground">Data types:</strong> contact identifiers (name, email, phone), professional history (resume, employment, references), authentication credentials, technical identifiers (IP, device ID), communications between Controller and candidates, and — with explicit candidate consent — background verification results.</p>
                    </Section>

                    <Section n="4" id="security" title="Security measures" testId="dpa-security">
                        <ul className="list-disc pl-5 space-y-1.5">
                            <li>Encryption in transit (TLS 1.2+) and at rest (AES-256).</li>
                            <li>Role-based access control; production access limited to on-call engineers.</li>
                            <li>Secrets isolated in a KMS-backed vault; no plaintext keys in code or environment dumps.</li>
                            <li>Continuous vulnerability scanning and patch cadence &lt; 30 days for high-severity CVEs.</li>
                            <li>Audit logs retained for 12 months.</li>
                            <li>Annual third-party penetration test.</li>
                        </ul>
                    </Section>

                    <Section n="5" id="subprocessors" title="Subprocessors" testId="dpa-subprocessors">
                        <p>
                            Processor may engage third-party subprocessors to deliver the platform. The current list is published at{" "}
                            <Link to="/subprocessors" className="underline text-foreground font-medium" data-testid="dpa-link-subprocessors">/subprocessors</Link>.
                        </p>
                        <p>
                            Processor will notify Controller at least <strong className="text-foreground">30 days</strong> before adding a new subprocessor that processes Personal Data. Controller may object in writing during the notice window; if the objection cannot be resolved, Controller may terminate the affected service without penalty.
                        </p>
                    </Section>

                    <Section n="6" id="cross-border" title="International transfers" testId="dpa-cross-border">
                        <p>Where Personal Data is transferred out of the EEA/UK, transfers are covered by the EU Standard Contractual Clauses (2021/914) and, for UK transfers, the UK IDTA — incorporated into this DPA by reference.</p>
                    </Section>

                    <Section n="7" id="breach" title="Breach notification" testId="dpa-breach">
                        <p>
                            Processor will notify Controller <strong className="text-foreground">without undue delay and no later than 72 hours</strong> after becoming aware of a Personal Data Breach affecting Controller data, including scope, categories affected, and remediation steps.
                        </p>
                        <p>Breach notifications are sent from <a href="mailto:security@simonaratalent.us" className="underline text-foreground font-medium">security@simonaratalent.us</a>.</p>
                    </Section>

                    <Section n="8" id="rights" title="Data subject rights assistance" testId="dpa-rights">
                        <p>Processor will assist Controller in responding to data subject requests (access, correction, deletion, portability, objection, restriction) within statutory timelines. Standard requests are handled within 30 days at no additional cost.</p>
                    </Section>

                    <Section n="9" id="deletion" title="Return &amp; deletion on termination" testId="dpa-deletion">
                        <p>Within <strong className="text-foreground">30 days</strong> of the termination or expiry of the underlying Terms of Service, Processor will either return all Personal Data to Controller in a machine-readable format or securely delete it, at Controller's election. Deletion certification available on request. Backups purged on their normal rotation (max 90 days).</p>
                    </Section>

                    <Section n="10" id="audit" title="Audits" testId="dpa-audit">
                        <p>Once per calendar year, Controller (or an independent auditor on Controller's behalf, bound by confidentiality) may request a summary of Processor's security posture including the most recent penetration-test attestation. On-site audits available on 30-day notice and subject to reasonable scope + fee.</p>
                    </Section>

                    <Section n="11" id="liability" title="Liability" testId="dpa-liability">
                        <p>The liability of each party arising out of or related to this DPA is subject to the limitations and exclusions of liability set out in the underlying Terms of Service, save for liability that cannot be limited under applicable law.</p>
                    </Section>

                    <Section n="12" id="governing" title="Governing law" testId="dpa-governing">
                        <p>This DPA is governed by the laws of the State of Delaware, USA, except to the extent that mandatory data-protection laws apply.</p>
                    </Section>

                    <div className="mt-8 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5 inline-flex items-start gap-3">
                        <ShieldCheck size={18} className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                        <p className="text-sm text-foreground">
                            <strong>Want this countersigned?</strong> Email{" "}
                            <a href="mailto:privacy@simonaratalent.us" className="underline">privacy@simonaratalent.us</a> and we'll return a signed copy on your paper or ours within two business days.
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
}
